/**
 * ClientPortalLocationsMap Hook
 *
 * Renders Google Maps with location circle dots colored by lease expiration urgency
 * and sized by property type + square footage.
 *
 * Color scheme:
 * - Red (#E11D48): 0-18 months until expiration
 * - Orange (#F97316): 19-36 months until expiration
 * - Blue (#0086e6): 37+ months or no expiration date
 *
 * Circle size by product type and square footage:
 * - Large: Office/Life Science >100k sf, Industrial >1M sf, Retail >20k sf
 * - Medium: Office/Life Science 25k-100k sf, Industrial 250k-1M sf, Retail 5k-20k sf
 * - Small: Office/Life Science <25k sf, Industrial <250k sf, Retail <5k sf
 *
 * For clustered markers, the most urgent (lowest months) expiration determines the color.
 */

// Expiration-based marker colors
const EXPIRATION_COLORS = {
  RED: "#E11D48", // 0-18 months
  ORANGE: "#F97316", // 19-36 months
  BLUE: "#0086e6", // 37+ months or no expiration
}

const getPinColorByExpiration = (monthsUntilExpiration) => {
  if (monthsUntilExpiration == null) return EXPIRATION_COLORS.BLUE
  if (monthsUntilExpiration <= 18) return EXPIRATION_COLORS.RED
  if (monthsUntilExpiration <= 36) return EXPIRATION_COLORS.ORANGE
  return EXPIRATION_COLORS.BLUE
}

// Colors used in tooltip and marker rendering
const TOOLTIP_COLORS = {
  ACTIVE_DEAL: "#0086e6", // matches dashboard chart "safe/positive" color
  WHITE: "#fff",
  TEXT_PRIMARY: "#111827", // gray-900
  TEXT_SECONDARY: "#374151", // gray-700
  TEXT_MUTED: "#6b7280", // gray-500
  BUTTON_BLUE: "#016fc4",
  STROKE: "#fff",
}

// Circle radii for each size tier (in SVG units)
const CIRCLE_SIZES = {
  LARGE: 18,
  MEDIUM: 13,
  SMALL: 10,
}

// Small uniform dot radius used in clustered/zoomed-out mode
const SMALL_DOT_RADIUS = 6

// Below this zoom level, show minimal labels (continent/country geometry only)
const MINIMAL_LABELS_ZOOM = 6

// Pure grayscale base style for the locations map. Cooler-than-neutral gray
// hex values give a faint brand-blue undertone without any actual saturation,
// so the colored heat-map dots/halos pop cleanly against the background.
// Using saturation: -100 strips any residual green/warm tint Google's default
// styling layers in (e.g. parks, POIs).
const GRAYSCALE_MAP_STYLES = [
  { stylers: [{ saturation: -100 }, { lightness: 8 }] },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [{ color: "#e6ebef" }],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [{ color: "#9ca5ad" }],
  },
  {
    featureType: "landscape",
    elementType: "geometry",
    stylers: [{ color: "#f4f5f7" }],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ color: "#ffffff" }],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [{ color: "#e6e8eb" }],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [{ color: "#a8acb1" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [{ color: "#eef0f2" }],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [{ color: "#dde1e5" }],
  },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [{ color: "#dde0e4" }],
  },
  {
    featureType: "administrative",
    elementType: "labels.text.fill",
    stylers: [{ color: "#a8acb1" }],
  },
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  // Neutral light gray (not green) so parks don't break the grayscale feel.
  {
    featureType: "poi.park",
    elementType: "geometry",
    stylers: [{ color: "#ecedef" }],
  },
  {
    featureType: "transit.line",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "#d0d3d8" }],
  },
  {
    featureType: "transit.station",
    elementType: "labels",
    stylers: [{ visibility: "simplified" }],
  },
]

// Stylers to layer on top of DESATURATED_MAP_STYLES when zoomed out, to suppress
// most textual labels while leaving boundary/road/water geometry intact.
const MINIMAL_LABELS_OVERRIDES = [
  {
    featureType: "administrative",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "water",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "landscape",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
]

// Less aggressive label suppression for zoomed-in view: keep country, state,
// and city labels (Google decides which cities show at which zoom — its
// heuristic surfaces majors first), but drop road names/numbers, neighborhoods,
// land parcels, and transit labels so the map stays calm under the dots. Also
// kills local-road geometry (the thin street grid that creates visual noise)
// and land-parcel boundaries — only highway/arterial geometry remains, giving
// the city outline without the street-level detail.
const MODERATE_LABELS_OVERRIDES = [
  {
    featureType: "road",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road.local",
    elementType: "geometry",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "road.arterial",
    elementType: "geometry",
    stylers: [{ visibility: "simplified" }],
  },
  {
    featureType: "administrative.neighborhood",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "administrative.land_parcel",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "landscape",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi",
    stylers: [{ visibility: "off" }],
  },
]

// Determine circle size based on product type and square footage
const getCircleRadius = (productType, sizeRaw) => {
  if (sizeRaw == null) return CIRCLE_SIZES.MEDIUM

  const type = (productType || "").toLowerCase()

  if (type.includes("office") || type.includes("life science")) {
    if (sizeRaw > 100000) return CIRCLE_SIZES.LARGE
    if (sizeRaw >= 25000) return CIRCLE_SIZES.MEDIUM
    return CIRCLE_SIZES.SMALL
  }

  if (type.includes("industrial")) {
    if (sizeRaw > 1000000) return CIRCLE_SIZES.LARGE
    if (sizeRaw >= 250000) return CIRCLE_SIZES.MEDIUM
    return CIRCLE_SIZES.SMALL
  }

  if (type.includes("retail")) {
    if (sizeRaw > 20000) return CIRCLE_SIZES.LARGE
    if (sizeRaw >= 5000) return CIRCLE_SIZES.MEDIUM
    return CIRCLE_SIZES.SMALL
  }

  // Default for other product types: use office thresholds
  if (sizeRaw > 100000) return CIRCLE_SIZES.LARGE
  if (sizeRaw >= 25000) return CIRCLE_SIZES.MEDIUM
  return CIRCLE_SIZES.SMALL
}

// Create a circle marker as a data-URI SVG. At high zoom (individual markers),
// the dot is sized by product-type / square-footage but wraps the same heat-map
// halo treatment as the clustered small dots — colored gradient + tight white
// bloom — so the visual language stays consistent across zoom levels.
const createCircleIcon = (fillColor, radius, google, scaleFactor = 1) => {
  const r = radius * scaleFactor
  const halo = Math.ceil(r * 7)
  const center = halo / 2
  const whiteR = r * 1.6
  const gradId = `circ-halo-${fillColor.replace("#", "")}`
  const whiteGradId = `circ-white-glow`

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${halo}" height="${halo}" viewBox="0 0 ${halo} ${halo}">
      <defs>
        <radialGradient id="${gradId}" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="${fillColor}" stop-opacity="0.5"/>
          <stop offset="50%" stop-color="${fillColor}" stop-opacity="0.2"/>
          <stop offset="100%" stop-color="${fillColor}" stop-opacity="0"/>
        </radialGradient>
        <radialGradient id="${whiteGradId}" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0"/>
          <stop offset="70%" stop-color="#ffffff" stop-opacity="0.5"/>
          <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="${center}" cy="${center}" r="${center}" fill="url(#${gradId})"/>
      <circle cx="${center}" cy="${center}" r="${whiteR}" fill="url(#${whiteGradId})"/>
      <circle cx="${center}" cy="${center}" r="${r}" fill="${fillColor}"/>
    </svg>
  `
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(halo, halo),
    anchor: new google.maps.Point(center, center),
  }
}

// Create a small uniform dot used at zoomed-out / clustered view so individual
// locations read as quiet "heat points" rather than full product-type circles.
// Wraps the dot in a layered halo: a tight white rim right at the dot edge
// followed by a translucent colored gradient — overlapping halos blend into
// the heat-map glow that clusters also produce.
const createSmallDotIcon = (fillColor, google, scaleFactor = 1) => {
  const r = SMALL_DOT_RADIUS * scaleFactor
  // Halo radius is 3.5x dot radius (SVG bbox = 7x dot diameter), so the dot
  // edge always sits at ~28.5% along the radial gradient. White-rim stops are
  // placed there to bloom right at the boundary regardless of dot size.
  const halo = Math.ceil(r * 7)
  const center = halo / 2
  const gradId = `dot-halo-${fillColor.replace("#", "")}`

  // White-glow circle is 1.6x the dot radius — tighter than before so the
  // glow only extends ~0.6x beyond the dot. Peak alpha sits at 70% offset
  // (just outside the dot edge at 62.5%) to bloom rather than rim.
  const whiteR = r * 1.6
  const whiteGradId = `dot-white-glow`

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${halo}" height="${halo}" viewBox="0 0 ${halo} ${halo}">
      <defs>
        <radialGradient id="${gradId}" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="${fillColor}" stop-opacity="0.5"/>
          <stop offset="50%" stop-color="${fillColor}" stop-opacity="0.2"/>
          <stop offset="100%" stop-color="${fillColor}" stop-opacity="0"/>
        </radialGradient>
        <radialGradient id="${whiteGradId}" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0"/>
          <stop offset="70%" stop-color="#ffffff" stop-opacity="0.5"/>
          <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="${center}" cy="${center}" r="${center}" fill="url(#${gradId})"/>
      <circle cx="${center}" cy="${center}" r="${whiteR}" fill="url(#${whiteGradId})"/>
      <circle cx="${center}" cy="${center}" r="${r}" fill="${fillColor}"/>
    </svg>
  `
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(halo, halo),
    anchor: new google.maps.Point(center, center),
  }
}

// Create a circle marker with an active-deal notification dot at top-right.
// Same heat-map halo treatment as createCircleIcon, plus a small blue
// indicator dot on the upper-right of the main circle.
const createCircleWithDotIcon = (
  fillColor,
  radius,
  google,
  scaleFactor = 1,
) => {
  const r = radius * scaleFactor
  const dotColor = TOOLTIP_COLORS.ACTIVE_DEAL
  const dotRadius = 4
  const halo = Math.ceil(r * 7)
  const center = halo / 2
  const whiteR = r * 1.6
  const gradId = `circ-d-halo-${fillColor.replace("#", "")}`
  const whiteGradId = `circ-d-white-glow`
  // Position indicator dot at the upper-right of the main circle (45° up).
  const dotCx = center + r * 0.7
  const dotCy = center - r * 0.7

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${halo}" height="${halo}" viewBox="0 0 ${halo} ${halo}">
      <defs>
        <radialGradient id="${gradId}" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="${fillColor}" stop-opacity="0.5"/>
          <stop offset="50%" stop-color="${fillColor}" stop-opacity="0.2"/>
          <stop offset="100%" stop-color="${fillColor}" stop-opacity="0"/>
        </radialGradient>
        <radialGradient id="${whiteGradId}" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#ffffff" stop-opacity="0"/>
          <stop offset="70%" stop-color="#ffffff" stop-opacity="0.5"/>
          <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <circle cx="${center}" cy="${center}" r="${center}" fill="url(#${gradId})"/>
      <circle cx="${center}" cy="${center}" r="${whiteR}" fill="url(#${whiteGradId})"/>
      <circle cx="${center}" cy="${center}" r="${r}" fill="${fillColor}"/>
      <circle cx="${dotCx}" cy="${dotCy}" r="${dotRadius}" fill="${dotColor}" stroke="${TOOLTIP_COLORS.STROKE}" stroke-width="1.5"/>
    </svg>
  `
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new google.maps.Size(halo, halo),
    anchor: new google.maps.Point(center, center),
  }
}

// Build light-themed tooltip HTML for a location pin
const buildTooltipContent = (location, t) => {
  const container = document.createElement("div")
  container.style.cssText =
    "padding:18px /* root-scale: legacy-px */ 10px /* root-scale: legacy-px */ 10px /* root-scale: legacy-px */ 10px /* root-scale: legacy-px */;width:260px /* root-scale: legacy-px */;font-family:'Inter',system-ui,-apple-system,sans-serif;cursor:pointer;"
  container.dataset.locationId = location.id

  // Active deal pill badge
  if (location.has_active_deal) {
    const badge = document.createElement("span")
    badge.style.cssText = `display:inline-block;background:${TOOLTIP_COLORS.ACTIVE_DEAL};color:${TOOLTIP_COLORS.WHITE};font-size:0.75rem /* root-scale: target */;font-weight:500;padding:1px /* root-scale: legacy-px */ 6px /* root-scale: legacy-px */;border-radius:3px /* root-scale: legacy-px */;margin-bottom:10px /* root-scale: legacy-px */;letter-spacing:0.02em;`
    badge.textContent = t("Active Deal Underway")
    container.appendChild(badge)
  }

  // Property name
  if (location.name) {
    const name = document.createElement("div")
    name.style.cssText = `color:${TOOLTIP_COLORS.TEXT_PRIMARY};font-size:1.125rem /* root-scale: target */;font-weight:600;line-height:1.2;margin-bottom:6px /* root-scale: legacy-px */;`
    name.textContent = location.name
    container.appendChild(name)
  }

  // Street address
  if (location.street) {
    const street = document.createElement("div")
    street.style.cssText = `color:${TOOLTIP_COLORS.TEXT_MUTED};font-size:0.75rem /* root-scale: target */;line-height:1.4;`
    street.textContent = location.street
    container.appendChild(street)
  }

  // City/state/zip
  if (location.city_state_zip) {
    const csz = document.createElement("div")
    csz.style.cssText = `color:${TOOLTIP_COLORS.TEXT_MUTED};font-size:0.75rem /* root-scale: target */;line-height:1.4;margin-bottom:10px /* root-scale: legacy-px */;`
    csz.textContent = location.city_state_zip
    container.appendChild(csz)
  }

  // Detail lines - each on its own line
  if (location.size && location.area_unit_abbr) {
    const sizeLine = document.createElement("div")
    sizeLine.style.cssText = `color:${TOOLTIP_COLORS.TEXT_SECONDARY};font-size:0.75rem /* root-scale: target */;line-height:1.5;`
    sizeLine.textContent = `${location.size} ${location.area_unit_abbr}`
    container.appendChild(sizeLine)
  }

  if (location.product_type) {
    const typeLine = document.createElement("div")
    typeLine.style.cssText = `color:${TOOLTIP_COLORS.TEXT_SECONDARY};font-size:0.75rem /* root-scale: target */;line-height:1.5;`
    typeLine.textContent = location.product_type
    container.appendChild(typeLine)
  }

  if (location.expires_at_formatted) {
    const lxdLine = document.createElement("div")
    lxdLine.style.cssText = `color:${TOOLTIP_COLORS.TEXT_SECONDARY};font-size:0.75rem /* root-scale: target */;line-height:1.5;margin-bottom:12px /* root-scale: legacy-px */;`
    lxdLine.textContent = `${t("LXD")} ${location.expires_at_formatted}`
    container.appendChild(lxdLine)
  }

  // "More" button - blue pill with white text
  const moreBtn = document.createElement("button")
  moreBtn.style.cssText = `color:${TOOLTIP_COLORS.WHITE};font-size:0.75rem /* root-scale: target */;font-weight:500;background:${TOOLTIP_COLORS.BUTTON_BLUE};border:none;outline:none;cursor:pointer;padding:0 12px /* root-scale: legacy-px */;border-radius:4px /* root-scale: legacy-px */;height:28px /* root-scale: legacy-px */;line-height:1.75rem /* root-scale: target */;`
  moreBtn.textContent = t("More")
  moreBtn.dataset.locationId = location.id
  container.appendChild(moreBtn)

  return container
}

export const ClientPortalLocationsMap = {
  async mounted() {
    this._locations = JSON.parse(this.el.dataset.locations || "[]")

    // Pull pattern: register the listener, then ask the server for the data.
    // push_event from the server is a fire-and-forget window event (no replay),
    // so emitting it from mount races against this hook being mounted. Asking
    // the server only once we're listening eliminates the race. Locations
    // arrives as a JSON string (push_event serializes the payload).
    this.handleEvent("update-locations-map", ({ locations }) => {
      const parsed =
        typeof locations === "string" ? JSON.parse(locations) : locations
      if (JSON.stringify(parsed) === JSON.stringify(this._locations)) return
      this._locations = parsed
      this.renderMap()
    })
    this.pushEvent("locations_map_ready")

    await this.renderMap()
  },

  async renderMap() {
    const locations = this._locations || []
    const t = (key) => window.I18n[key]
    const noLocations = t("No locations to display")
    const failedToLoadMap = t("Failed to load map")
    const locationSingular = t("location")
    const locationPlural = t("locations")

    try {
      await window.Enaia.loadGoogleMaps()

      const baseStyles = GRAYSCALE_MAP_STYLES
      const minimalStyles = [...baseStyles, ...MINIMAL_LABELS_OVERRIDES]
      const moderateStyles = [...baseStyles, ...MODERATE_LABELS_OVERRIDES]
      const STARTING_ZOOM = 4

      const map = new google.maps.Map(this.el, {
        zoom: STARTING_ZOOM,
        minZoom: 3,
        backgroundColor: "#e8eef1",
        center: { lat: 39.8283, lng: -98.5795 },
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        zoomControl: true,
        zoomControlOptions: {
          position: google.maps.ControlPosition.RIGHT_CENTER,
        },
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        styles:
          STARTING_ZOOM < MINIMAL_LABELS_ZOOM ? minimalStyles : moderateStyles,
        disableDefaultUI: false,
        gestureHandling: "greedy",
      })

      // Track which style mode is currently applied so we only swap styles
      // when crossing the zoom threshold (not on every zoom tick).
      let currentStyleMode =
        STARTING_ZOOM < MINIMAL_LABELS_ZOOM ? "minimal" : "moderate"
      const applyZoomBasedStyles = () => {
        const z = map.getZoom()
        const next = z < MINIMAL_LABELS_ZOOM ? "minimal" : "moderate"
        if (next === currentStyleMode) return
        currentStyleMode = next
        map.setOptions({
          styles: next === "minimal" ? minimalStyles : moderateStyles,
        })
      }

      const self = this
      let currentMarkers = []
      let initialBounds = null
      let initialZoom = null

      // Shared InfoWindow for hover tooltips
      // Circles are center-anchored, so offset the InfoWindow above the circle
      const activeInfoWindow = new google.maps.InfoWindow({
        disableAutoPan: false,
        pixelOffset: new google.maps.Size(0, -4),
      })
      let closeTimeout = null
      // Delays the tooltip on hover so a quick mouse pass over many dots
      // doesn't strobe InfoWindows. Icon scale-up still fires immediately.
      const HOVER_OPEN_DELAY_MS = 400
      let openTimeout = null
      let hoveredMarker = null
      // Stacking: single dots sit above cluster halos so a single dot's hover
      // hit area never gets eaten by an overlapping cluster's wider halo.
      // Hovered marker bumps even higher so it stays above unhovered peers.
      const Z_CLUSTER = 100
      const Z_SINGLE = 200
      const Z_HOVER = 300
      // Helper to get the correct icon (with or without dot) for a location
      // Circle radius is determined by product type + square footage
      const iconFor = (loc, color, scaleFactor = 1) => {
        const radius = getCircleRadius(loc.product_type, loc.size_raw)
        return loc.has_active_deal
          ? createCircleWithDotIcon(color, radius, google, scaleFactor)
          : createCircleIcon(color, radius, google, scaleFactor)
      }

      const resetHoveredMarker = () => {
        if (hoveredMarker) {
          const prev = hoveredMarker
          const fn = prev.iconFn || iconFor
          prev.marker.setIcon(fn(prev.location, prev.pinColor))
          if (prev.baseZ !== undefined) prev.marker.setZIndex(prev.baseZ)
          hoveredMarker = null
        }
      }

      const scheduleClose = () => {
        closeTimeout = setTimeout(() => {
          activeInfoWindow.close()
          resetHoveredMarker()
        }, 150)
      }
      const cancelClose = () => {
        if (closeTimeout) {
          clearTimeout(closeTimeout)
          closeTimeout = null
        }
      }
      const cancelOpen = () => {
        if (openTimeout) {
          clearTimeout(openTimeout)
          openTimeout = null
        }
      }
      const scheduleOpen = (marker, location) => {
        cancelOpen()
        openTimeout = setTimeout(() => {
          activeInfoWindow.setContent(buildTooltipContent(location, t))
          activeInfoWindow.open(map, marker)
        }, HOVER_OPEN_DELAY_MS)
      }

      // When InfoWindow DOM is ready, attach hover listeners to keep it open
      google.maps.event.addListener(activeInfoWindow, "domready", () => {
        const iwContainer = document.querySelector(
          "#locations-map .gm-style-iw-c",
        )
        if (iwContainer) {
          iwContainer.addEventListener("mouseenter", cancelClose)
          iwContainer.addEventListener("mouseleave", scheduleClose)
        }

        // Wire tooltip click to open the location modal (whole tooltip is clickable)
        const tooltipContainer = document.querySelector(
          "#locations-map .gm-style-iw-d div[data-location-id]",
        )
        if (tooltipContainer) {
          tooltipContainer.addEventListener("click", () => {
            const id = tooltipContainer.dataset.locationId
            activeInfoWindow.close()
            resetHoveredMarker()
            self.pushEvent("show_details", { id })
          })
        }
      })

      // Toggle animation on/off
      const animated = true
      const animationDuration = 700
      const FLY_TO_PADDING_PX = 64
      const FLY_TO_MAX_ZOOM = 15

      // Compute the zoom level that fits a LatLngBounds inside the current
      // viewport with the given pixel padding. Mirrors what map.fitBounds()
      // would pick, but returned as a number so we can animate to it with
      // our own easing instead of Google's built-in transition.
      const computeFitZoom = (bounds, paddingPx) => {
        const WORLD_PX = 256

        const latRad = (lat) => {
          const sin = Math.sin((lat * Math.PI) / 180)
          const r = Math.log((1 + sin) / (1 - sin)) / 2
          return Math.max(Math.min(r, Math.PI), -Math.PI) / 2
        }

        const ne = bounds.getNorthEast()
        const sw = bounds.getSouthWest()
        const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI
        const lngDiff = ne.lng() - sw.lng()
        const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360

        const div = map.getDiv()
        const effW = Math.max(1, div.offsetWidth - 2 * paddingPx)
        const effH = Math.max(1, div.offsetHeight - 2 * paddingPx)

        const latZoom = Math.log2(
          effH / WORLD_PX / Math.max(latFraction, 1e-12),
        )
        const lngZoom = Math.log2(
          effW / WORLD_PX / Math.max(lngFraction, 1e-12),
        )
        return Math.min(latZoom, lngZoom, FLY_TO_MAX_ZOOM)
      }

      // Get reference to reset button
      const resetBtn = document.getElementById("reset-zoom-btn")

      // Helper function for animated camera movement
      const animateCamera = (targetLat, targetLng, targetZoom, duration) => {
        if (!animated) {
          map.moveCamera({
            center: { lat: targetLat, lng: targetLng },
            zoom: targetZoom,
          })
          return
        }

        const startZoom = map.getZoom()
        const startCenter = map.getCenter()
        const startLat = startCenter.lat()
        const startLng = startCenter.lng()
        const startTime = performance.now()

        // Ease-in-out cubic for smooth animation
        const easeInOutCubic = (t) =>
          t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

        const animate = (currentTime) => {
          const elapsed = currentTime - startTime
          const progress = Math.min(elapsed / duration, 1)
          const easedProgress = easeInOutCubic(progress)

          map.moveCamera({
            center: {
              lat: startLat + (targetLat - startLat) * easedProgress,
              lng: startLng + (targetLng - startLng) * easedProgress,
            },
            zoom: startZoom + (targetZoom - startZoom) * easedProgress,
          })

          if (progress < 1) {
            requestAnimationFrame(animate)
          }
        }

        requestAnimationFrame(animate)
      }

      // Function to show/hide reset button based on zoom level
      const updateResetButton = () => {
        if (!resetBtn || !initialZoom) return
        const currentZoom = map.getZoom()
        if (currentZoom > initialZoom + 1) {
          resetBtn.classList.remove("hidden")
          resetBtn.classList.add("flex")
        } else {
          resetBtn.classList.add("hidden")
          resetBtn.classList.remove("flex")
        }
      }

      // Function to render markers based on current zoom level
      const renderMarkers = () => {
        // Clear existing markers
        currentMarkers.forEach((m) => m.setMap(null))
        currentMarkers = []

        const zoom = map.getZoom()
        // At zoom >= 10, show individual markers; below that, show clusters
        const shouldCluster = zoom < 10

        if (shouldCluster) {
          // Threshold grows by 1.6× per zoom-out step from 9, so the merge
          // radius widens as the map covers more area without snowballing:
          //   zoom 9 → 0.4°   (≈ 45km, near-city)
          //   zoom 8 → 0.64°  (metro)
          //   zoom 7 → 1.02°  (regional — Tampa+Orlando cluster, Miami stays separate)
          //   zoom 6 → 1.64°  (state-wide subregions)
          //   zoom 5 → 2.62°  (multi-state)
          //   zoom 4 → 4.19°  (continental subregions)
          const dynamicThreshold = 0.4 * Math.pow(1.6, Math.max(0, 9 - zoom))
          const clusters = self.clusterLocations(locations, dynamicThreshold)

          clusters.forEach((cluster) => {
            const position = { lat: cluster.lat, lng: cluster.lng }
            const count = cluster.locations.length

            // For clusters, use the most urgent (lowest months) expiration to determine color
            const minMonths = Math.min(
              ...cluster.locations.map(
                (loc) => loc.months_until_expiration ?? Infinity,
              ),
            )
            const pinColor = getPinColorByExpiration(
              minMonths === Infinity ? null : minMonths,
            )

            if (count === 1) {
              const location = cluster.locations[0]
              // In clustered/zoomed-out view, render single locations as small
              // uniform dots (heat-points) rather than product-type circles.
              const smallDotIconFn = (loc, color, scale = 1) =>
                createSmallDotIcon(color, google, scale)
              const marker = new google.maps.Marker({
                position,
                map,
                title: location.name,
                icon: smallDotIconFn(location, pinColor),
                zIndex: Z_SINGLE,
              })
              marker.addListener("mouseover", () => {
                cancelClose()
                if (hoveredMarker && hoveredMarker.marker !== marker) {
                  resetHoveredMarker()
                }
                marker.setIcon(smallDotIconFn(location, pinColor, 1.6))
                marker.setZIndex(Z_HOVER)
                hoveredMarker = {
                  marker,
                  pinColor,
                  location,
                  iconFn: smallDotIconFn,
                  baseZ: Z_SINGLE,
                }
                scheduleOpen(marker, location)
              })
              marker.addListener("mouseout", () => {
                cancelOpen()
                scheduleClose()
              })
              marker.addListener("click", () =>
                self.pushEvent("show_details", { id: location.id }),
              )
              currentMarkers.push(marker)
            } else {
              const locationLabel =
                count === 1 ? locationSingular : locationPlural
              const marker = new google.maps.Marker({
                position,
                map,
                icon: self.createClusterIcon(count, pinColor),
                title: `${count} ${locationLabel}`,
                zIndex: Z_CLUSTER,
              })
              marker.addListener("mouseover", () => {
                marker.setIcon(self.createClusterIcon(count, pinColor, 1.3))
              })
              marker.addListener("mouseout", () => {
                marker.setIcon(self.createClusterIcon(count, pinColor))
              })
              // On click, zoom in to show individual markers
              marker.addListener("click", () => {
                const clusterBounds = new google.maps.LatLngBounds()
                cluster.locations.forEach((loc) => {
                  clusterBounds.extend({
                    lat: parseFloat(loc.lat),
                    lng: parseFloat(loc.lng),
                  })
                })

                const targetCenter = clusterBounds.getCenter()
                const targetZoom = computeFitZoom(
                  clusterBounds,
                  FLY_TO_PADDING_PX,
                )

                animateCamera(
                  targetCenter.lat(),
                  targetCenter.lng(),
                  targetZoom,
                  animationDuration,
                )
              })
              currentMarkers.push(marker)
            }
          })
        } else {
          // Show individual markers at high zoom
          locations.forEach((location) => {
            if (!location.lat || !location.lng) return
            const pinColor = getPinColorByExpiration(
              location.months_until_expiration,
            )
            const position = {
              lat: parseFloat(location.lat),
              lng: parseFloat(location.lng),
            }

            const marker = new google.maps.Marker({
              position,
              map,
              title: location.name,
              icon: iconFor(location, pinColor),
              zIndex: Z_SINGLE,
            })
            marker.addListener("mouseover", () => {
              cancelClose()
              if (hoveredMarker && hoveredMarker.marker !== marker) {
                resetHoveredMarker()
              }
              marker.setIcon(iconFor(location, pinColor, 1.25))
              marker.setZIndex(Z_HOVER)
              hoveredMarker = {
                marker,
                pinColor,
                location,
                iconFn: iconFor,
                baseZ: Z_SINGLE,
              }
              scheduleOpen(marker, location)
            })
            marker.addListener("mouseout", () => {
              cancelOpen()
              scheduleClose()
            })
            marker.addListener("click", () =>
              self.pushEvent("show_details", { id: location.id }),
            )
            currentMarkers.push(marker)
          })
        }
      }

      // Initial render
      const bounds = new google.maps.LatLngBounds()
      locations.forEach((loc) => {
        if (loc.lat && loc.lng) {
          bounds.extend({ lat: parseFloat(loc.lat), lng: parseFloat(loc.lng) })
        }
      })

      if (locations.length > 0) {
        initialBounds = bounds
        map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 })
        google.maps.event.addListenerOnce(map, "idle", () => {
          if (map.getZoom() > 15) map.setZoom(15)
          initialZoom = map.getZoom()
          renderMarkers()
        })
      }

      // Re-render markers and swap label-density styles when zoom changes
      map.addListener("zoom_changed", () => {
        renderMarkers()
        updateResetButton()
        applyZoomBasedStyles()
      })

      // Reset button click handler - animate back to initial view
      if (resetBtn) {
        resetBtn.addEventListener("click", () => {
          if (!initialBounds || !initialZoom) return
          const targetCenter = initialBounds.getCenter()
          animateCamera(
            targetCenter.lat(),
            targetCenter.lng(),
            initialZoom,
            animationDuration * 0.9,
          )
        })
      }
    } catch (error) {
      console.error("Error loading Google Maps:", error)
      this.el.innerHTML = `<div class="flex items-center justify-center h-full text-red-500">${failedToLoadMap}</div>`
    }
  },

  // Group locations within a threshold distance (in degrees, ~0.5 deg ≈ 50km)
  clusterLocations(locations, threshold) {
    const clusters = []

    locations.forEach((location) => {
      if (!location.lat || !location.lng) return

      const lat = parseFloat(location.lat)
      const lng = parseFloat(location.lng)

      // Find existing cluster within threshold
      let foundCluster = clusters.find(
        (c) =>
          Math.abs(c.lat - lat) < threshold &&
          Math.abs(c.lng - lng) < threshold,
      )

      if (foundCluster) {
        foundCluster.locations.push(location)
        // Update centroid
        const n = foundCluster.locations.length
        foundCluster.lat = (foundCluster.lat * (n - 1) + lat) / n
        foundCluster.lng = (foundCluster.lng * (n - 1) + lng) / n
      } else {
        clusters.push({
          lat,
          lng,
          locations: [location],
        })
      }
    })

    return clusters
  },

  // Create a circular cluster icon with a translucent radial-gradient halo
  // around it; overlapping halos blend visually into a heat-map-style glow.
  // Badge size scales sublinearly with `count` so a 112-cluster reads denser
  // than a 6-cluster but doesn't overwhelm the viewport.
  // `scaleFactor` grows the whole cluster (badge + halo together) on hover —
  // mirrors the small-dot hover behavior so single dots and clusters animate
  // the same way under the cursor.
  createClusterIcon(count, color, scaleFactor = 1) {
    const badgeR = Math.round((7 + Math.log2(count) * 3) * scaleFactor)
    // Font size tracks badgeR almost 1:1 so a "6" badge reads visibly larger
    // than a "2" badge — earlier multipliers got swallowed by the min-size
    // floor at the low end of the range.
    const fontSize = Math.max(10, Math.round(badgeR * 0.95))
    const halo = Math.ceil(badgeR * 7)
    const center = halo / 2
    const gradId = `cl-halo-${count}-${color.replace("#", "")}`

    // White-glow circle is 1.6x the badge radius — tighter than the colored
    // halo. Peak sits at 70% offset (just outside the badge edge at 62.5%)
    // to read as a soft bloom rather than a hard rim.
    const whiteR = badgeR * 1.6
    const whiteGradId = `cl-white-glow-${count}`

    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${halo}" height="${halo}" viewBox="0 0 ${halo} ${halo}">
        <defs>
          <radialGradient id="${gradId}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="${color}" stop-opacity="0.5"/>
            <stop offset="50%" stop-color="${color}" stop-opacity="0.2"/>
            <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
          </radialGradient>
          <radialGradient id="${whiteGradId}" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stop-color="#ffffff" stop-opacity="0"/>
            <stop offset="70%" stop-color="#ffffff" stop-opacity="0.5"/>
            <stop offset="100%" stop-color="#ffffff" stop-opacity="0"/>
          </radialGradient>
        </defs>
        <circle cx="${center}" cy="${center}" r="${center}" fill="url(#${gradId})"/>
        <circle cx="${center}" cy="${center}" r="${whiteR}" fill="url(#${whiteGradId})"/>
        <circle cx="${center}" cy="${center}" r="${badgeR}" fill="${color}"/>
        <text x="${center}" y="${center}" text-anchor="middle" dominant-baseline="central" fill="#FFFFFF" font-size="${fontSize}" font-weight="500" font-family="Arial, sans-serif">${count}</text>
      </svg>
    `
    return {
      url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(halo, halo),
      anchor: new google.maps.Point(center, center),
    }
  },
}
